import './TestAnalysis.css';

import * as React from 'react';

import { RouteComponentProps, Link } from 'react-router-dom';
import { PageContent, PageTitle } from '../PageContent/PageContent';
import Loading from '../Loading/Loading';
import { getPTAnalysis, getIAAnalysis, getDetailPTScoresByTest } from '../../services/practice-test-scoring/practice-test-service';
import TestCard from './components/TestCard/TestCardContainer';
import { SimpleTimer } from '../../services/utils/simple-timer';
import { Context, Resource, UserActivityEx, sendUserActivity } from '../../services/liveview/liveview-service';
import { getTestDisplayName, isActTest, isInterimAssessment, SAT2024 } from "../../services/utils/practice-test-util";
import SATTestAnalysis from "./SATTestAnalysis";

export interface Props {
}

export interface StoreProps {
}

interface State {
    isLoaded: boolean;
    testData: any;
    testName: string;
    testTitle: string;
}

export type AllProps = Props & StoreProps & RouteComponentProps<any>;

export class TestAnalysis extends React.Component<AllProps, State> {
  simpleTimer = new SimpleTimer()

  constructor(props: AllProps) {
    super(props);
    this.state = {
        isLoaded: false,
        testData: [],
        testName: props.match.params.testName,
        testTitle: '',
    };
  }

  public componentDidMount() {
    let testName = this.props.match.params.testName
    let testId = testName
    let testDisplayName = getTestDisplayName(testName)
    let isIATest = isInterimAssessment(testName)

    if (!isIATest) {
        getPTAnalysis(testId).then((res) => {
            this.setState({
                testData: res,
                isLoaded: true,
                testTitle: testDisplayName
            })
        })
    } 
    else {
        getIAAnalysis(testId).then((res) => {
            this.setState({
                testData: res,
                isLoaded: true,
                testTitle: testDisplayName,
            })
        })
    }
  }

  componentWillUnmount() {
    this.sendStats()
  }

  /**
   * In the Practice tests a user can visit the analysis view multiple times
   * so we nee to allow multiple entries for this resource, hence the 
   * sendUseractivity has a boolean flag set to true.
   */
  sendStats() {
    let resource = Resource.ANALYSIS
    let {testName} = this.state
    let seconds = this.simpleTimer.diffInSeconds()
    let ended = seconds
    let context = Context.TEST
    let contextId = testName
    let category = "Analysis"
    let resourceId = `${testName}`
    let userActivityEx: UserActivityEx = {
        context, contextId, 
        resource, resourceId, 
        category,
        ended
    }
    sendUserActivity(userActivityEx, true)
    this.simpleTimer.reset()
  }
 
  public render() {
    const { testData, isLoaded, testTitle } = this.state;
    const titleAllCaps = testTitle.toUpperCase();
    if (!isLoaded) {
        return <Loading />;
    }
    const testName = this.props.match.params.testName;
    const link = '/app/practice-tests/scoring/' + testName;
    const testType = getTestTypeBasedOnName(testName)
    const summaryLink = `/print/practice-tests/summary/${testType}/${testName}`
    let type = 'ACT';

    if (testName.startsWith('SAT') || testName === 'DiagnosticSAT' ) {
        type = 'SAT';
    }

    const sortByTotal = (a, b) => b.total - a.total;

    let newTestTitle = getTestDisplayName(testName)
    let newTitleAllCaps = newTestTitle.toLocaleUpperCase()

    // Note that this test type is generic here and could be ACT | EnhancedACT | IA | SAT
    if (type === 'ACT') {
        // ACT| EnhancedACT | IA Practice Tests
        const readingLessonOrder = [
            'Prose Fiction', 'Social Science', 'Humanities', 'Natural Science',
            'Single Passages', 'Dual Passages',
        ];
        const readingAnalysisFixedOrder = readingLessonOrder.map( lessonText => 
            testData.readingAnalysis.find(r => r.lessonId.includes(lessonText))
        );

        testData.englishAnalysis.sort(sortByTotal);
        testData.mathAnalysis.sort(sortByTotal);
        testData.scienceAnalysis.sort(sortByTotal);

        return (
            <PageContent className="analysis-container">
            <PageTitle title={`${newTestTitle} Analysis`}>
                <div className="navigation-button-container">
                    <div className="pr-2">
                        <SummaryLink link={summaryLink}/>
                    </div>
                    <div>
                        <Link className="title-navigation-buttons btn btn--text-white btn--bg-green" to={link} title={`Back to ${newTitleAllCaps} Scoring`}>
                            BACK TO {newTitleAllCaps} SCORING
                        </Link>
                    </div>
                </div>
            </PageTitle>
            <div className="collapsable-card  practicetests-domain-analysis">
                    <div className="collapsable-card__header">
                        <div className="lesson-list-card-header purple">
                            <span className="ribbon purple">
                            <img alt="" src="/assets/images/icons/subjects/ico-subject-english.svg" width="28" height="27" />
                            </span>
                            <h3 className="mb-0">English</h3>
                        </div>
                    </div>
                    <div className="collapsable-card__body">
                        <TestCard
                            backgroundColor="purple-header"
                            testData={testData.englishAnalysis}
                            subject="English"
                        />
                    </div>
                </div>

                <div className="collapsable-card  practicetests-domain-analysis">
                    <div className="collapsable-card__header">
                        <div className="lesson-list-card-header blue">
                            <span className="ribbon blue">
                            <img alt="" src="/assets/images/icons/subjects/ico-subject-math.svg" width="28" height="27" />
                            </span>
                            <h3 className="mb-0">Math</h3>
                        </div>
                    </div>
                    <div className="collapsable-card__body">
                        <TestCard
                            backgroundColor="blue-header"
                            testData={testData.mathAnalysis}
                            subject="Math"
                        />
                    </div>
                </div>

                <div className="collapsable-card  practicetests-domain-analysis">
                    <div className="collapsable-card__header">
                        <div className="lesson-list-card-header orange">
                            <span className="ribbon orange">
                            <img alt="" src="/assets/images/icons/subjects/ico-subject-reading.svg" width="28" height="27" />
                            </span>
                            <h3 className="mb-0">Reading</h3>
                        </div>
                    </div>
                    <div className="collapsable-card__body">
                        <TestCard
                            backgroundColor="orange-header"
                            testData={readingAnalysisFixedOrder}
                            subject="Reading"
                        />
                    </div>
                </div>

                <div className="collapsable-card  practicetests-domain-analysis">
                    <div className="collapsable-card__header">
                        <div className="lesson-list-card-header green">
                            <span className="ribbon green">
                            <img alt="" src="/assets/images/icons/subjects/ico-subject-science.svg" width="28" height="27" />
                            </span>
                            <h3 className="mb-0">Science</h3>
                        </div>
                    </div>
                    <div className="collapsable-card__body">
                        <TestCard
                            backgroundColor="green-header"
                            testData={testData.scienceAnalysis}
                            subject="Science"
                        />
                    </div>
                </div>
            </PageContent>
        );
    } 
    else {
        // SAT Practice Tests
        testData.readingAnalysis.sort(sortByTotal);
        testData.writingAndLanguageAnalysis.sort(sortByTotal);
        testData.mathCalculatorAnalysis.sort(sortByTotal);
        testData.mathNoCalculatorAnalysis.sort(sortByTotal);

        return (
            <PageContent className="analysis-container">
                <PageTitle title={`${newTestTitle} Analysis`}>
                        <div className="navigation-button-container">
                            <div className="pr-2">
                                <SummaryLink link={summaryLink}/>
                            </div>
                            <div>
                                <Link className="title-navigation-buttons btn btn--text-white btn--bg-green" to={link} title={`Back to ${newTitleAllCaps} Scoring`}>
                                    BACK TO {newTitleAllCaps} SCORING
                                </Link>
                            </div>
                        </div>
                </PageTitle>

                <SATTestAnalysis testName={testName}/>

            </PageContent>
        );
    }
  }

}

// OT: Based on Rustam's hack :)
function getTestTypeBasedOnName(testName: string) {
    let testType = "?"

    if(testName === "Diagnostic" || testName.startsWith("ACT") || testName.includes("EnhancedACT")) {
        testType = "ACT"
    } else if (testName === "DiagnosticSAT" || testName.startsWith("SAT")) {
        testType = "SAT"
    } else {
        // It's an IA Test
        testType = "IA"
    }

    return testType
}

function SummaryLink(props) {
    let {link} = props

    return (
        <Link to={link} target="blank" title="View Report">
            <button className="title-navigation-buttons btn btn--bg-blue mb-2">
                VIEW REPORT
            </button>
        </Link>
    )
}
